import { useState } from 'react';
import FavoriteButton from '@/components/feature/FavoriteButton';
import { verifiedProperties } from '@/mocks/properties';
import type { PropertyHeroData } from '@/pages/property-detail/types';

type Props = {
  data: PropertyHeroData;
  onViewPhotos: () => void;
};

export default function PropertyHero({ data, onViewPhotos }: Props) {
  const [videoOpen, setVideoOpen] = useState(false);
  const property = verifiedProperties.find((p) => p.id === data.id);
  const primaryArea = property?.area;
  const secondaryAreas = property?.communities.filter((c) => c !== primaryArea) ?? [];
  const heroTextShadow = '0 2px 18px rgba(0,0,0,0.48), 0 1px 2px rgba(0,0,0,0.3)';
  const heroReadabilityMask = {
    background: [
      'radial-gradient(ellipse at 18% 31%, rgba(13,17,20,0.52) 0%, rgba(13,17,20,0.32) 24%, rgba(13,17,20,0.12) 44%, rgba(13,17,20,0) 64%)',
      'radial-gradient(ellipse at 18% 91%, rgba(13,17,20,0.38) 0%, rgba(13,17,20,0.18) 30%, rgba(13,17,20,0) 58%)',
      'linear-gradient(0deg, rgba(13,17,20,0.18) 0%, rgba(13,17,20,0.06) 20%, rgba(13,17,20,0) 36%)',
    ].join(', '),
  };

  return (
    <section className="relative w-full h-screen min-h-[600px] max-h-[960px] overflow-hidden">
      <FavoriteButton
        id={data.id}
        showLabel
        className="absolute top-28 right-5 md:top-32 md:right-10 z-20"
      />
      <div className="absolute inset-0 w-full h-full">
        <img
          src={data.heroImage}
          alt={`${data.name} \u2014 ${data.location}`}
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0" style={heroReadabilityMask} />
      </div>

      <div className="relative z-10 h-full flex flex-col justify-between px-5 md:px-10 pt-28 md:pt-32 pb-10 max-w-[1400px] mx-auto w-full">
        <div>
          <p
            className="text-[11px] uppercase tracking-[0.28em] text-background-100/90 mb-3"
            style={{ textShadow: heroTextShadow }}
          >
            {data.location}{data.area ? ` \u00b7 ${data.area}` : ''}
          </p>
          {primaryArea && (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center bg-background-50 text-foreground-950 px-3 py-1.5 rounded-lg text-[11px] uppercase tracking-[0.18em] shadow-sm">
                {primaryArea}
              </span>
              {secondaryAreas.map((area) => (
                <span
                  key={area}
                  className="inline-flex items-center border border-background-100/60 text-background-50 px-3 py-1.5 rounded-lg text-[11px] uppercase tracking-[0.18em]"
                >
                  {area}
                </span>
              ))}
            </div>
          )}
          <h1
            className="font-heading font-normal text-background-50 text-[44px] md:text-[72px] lg:text-[88px] leading-[0.97] tracking-normal max-w-[46rem] origin-left scale-x-[0.96]"
            style={{ textShadow: heroTextShadow, fontFeatureSettings: '"kern" 1, "liga" 0', fontVariantLigatures: 'none' }}
          >
            {data.name}
          </h1>

          <div className="mt-6 md:mt-8 flex flex-wrap gap-5 md:gap-8">
            {[
              { label: 'Bedrooms', value: data.bedrooms },
              { label: 'Bathrooms', value: data.bathrooms },
              { label: 'Sleeps', value: data.sleeps },
            ].map((s) => (
              <div key={s.label} className="flex flex-col gap-0.5">
                <span
                  className="text-background-50/85 text-[11px] uppercase tracking-[0.22em]"
                  style={{ textShadow: heroTextShadow }}
                >
                  {s.label}
                </span>
                <span
                  className="text-background-50 font-heading text-xl md:text-2xl leading-tight"
                  style={{ textShadow: heroTextShadow }}
                >
                  {s.value}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col lg:flex-row items-end lg:items-center justify-end lg:justify-start gap-4">
          <button
            type="button"
            onClick={onViewPhotos}
            className="inline-flex items-center gap-2 bg-[#EFE4D6] text-foreground-950 px-5 py-3 rounded-lg text-sm font-medium cursor-pointer shadow-[0_14px_30px_rgba(0,0,0,0.22)] hover:bg-[#E9DCCB] transition-colors whitespace-nowrap"
          >
            <i className="ri-gallery-line" />
            View Photos
          </button>

          {data.videoUrl && (
            <button
              type="button"
              onClick={() => setVideoOpen(true)}
              className="inline-flex items-center gap-2 border border-[#DED0BE] bg-[#EFE4D6]/90 text-foreground-950 px-5 py-3 rounded-lg text-sm cursor-pointer shadow-[0_14px_30px_rgba(0,0,0,0.18)] backdrop-blur-[2px] hover:bg-[#E9DCCB] transition-colors whitespace-nowrap"
            >
              <i className="ri-play-circle-line" />
              Watch Video
            </button>
          )}

          <a
            href="#booking"
            className="lg:ml-auto relative inline-flex items-center gap-3 bg-accent-500 text-background-50 hover:bg-accent-600 pl-4 pr-5 py-2.5 rounded-lg text-sm cursor-pointer transition-colors whitespace-nowrap shadow-[0_14px_30px_rgba(0,0,0,0.18)]"
          >
            <span className="-mt-5 w-10 h-10 flex items-center justify-center rounded-lg bg-background-50 text-accent-700 border border-accent-200 shadow-sm">
              <i className="ri-calendar-check-line text-lg" />
            </span>
            Book This Property
          </a>
        </div>
      </div>

      {videoOpen && data.videoUrl && (
        <div
          className="fixed inset-0 z-[100] bg-foreground-950/90 flex items-center justify-center p-4"
          onClick={() => setVideoOpen(false)}
        >
          <div
            className="relative w-full max-w-4xl aspect-video"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              src={data.videoUrl}
              className="absolute inset-0 w-full h-full rounded-xl"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title={`${data.name} \u2014 Video`}
            />
            <button
              type="button"
              onClick={() => setVideoOpen(false)}
              className="absolute -top-10 right-0 text-background-50 text-sm cursor-pointer flex items-center gap-1 hover:text-background-200"
            >
              <i className="ri-close-line text-base" />
              Close
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
